import React, { useEffect, useState } from 'react';
import {
    BooleanInput,
    Create,
    CreateButton,
    Datagrid,
    AutocompleteArrayInput,
    Edit,
    FunctionField,
    List,
    NumberField,
    NumberInput,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    maxValue,
    minValue,
    number,
    required,
    useNotify,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FormToolbar } from '../../common/FormToolbar';
import { schoolApi } from '../../common/schoolApi';
import { PaperPanel } from './PaperPanel';
import { GeneratePanel } from './GeneratePanel';
import { BankPanel } from './BankPanel';

/**
 * School tests -- the builder. Phase 4, task 4.8, over the API of task 4.3.
 *
 * WHO SEES WHAT (decided 2026-09-11, option 1), enforced by the API; this
 * screen only reflects it:
 *   - a teacher's drafts are theirs alone
 *   - a `ready` test is visible to every teacher, who may duplicate it
 *   - the principal sees every test and changes only their own
 * `can_edit` and `can_edit_questions` on each record are the API's answer,
 * and the screen goes read-only on them rather than working it out itself.
 *
 * The details form saves only the fields it owns (see `transform`). Status
 * moves through buttons, because each move has its own rule: ready needs a
 * question; an assigned test cannot go back to draft.
 */

const STATUS_COLOUR: Record<string, 'default' | 'success' | 'warning'> = { draft: 'warning', ready: 'success', archived: 'default' };

// `subject_id` is NOT here: the API derives it from `subject_ids` (one
// subject chosen = that subject, several = mixed). Sending both would let the
// form and the API disagree about what the test is about.
const EDITABLE = [
    'name', 'exam_id', 'subject_ids', 'language', 'difficulty', 'time_limit',
    'target_question_count', 'passing_percentage', 'description', 'instructions',
];
const transform = (data: any) => {
    const out: any = {};
    EDITABLE.forEach(k => {
        if (k in data) out[k] = data[k] === '' ? null : data[k];
    });
    return out;
};

type Choice = { id: number; name: string };

/**
 * Subjects and exams, from the builder's own taxonomy endpoint -- teachers
 * cannot read /subject or /exam.
 *
 * The counts come back with them, so each choice says how much the bank
 * actually holds. A teacher picking "Chemistry (0)" learns before they
 * generate why nothing came out, instead of after.
 */
const useTaxonomy = () => {
    const [subjects, setSubjects] = useState<Choice[]>([]);
    const [exams, setExams] = useState<Choice[]>([]);
    useEffect(() => {
        schoolApi
            .get('school-test/taxonomy')
            .then(r => {
                const label = (x: any) => ({ id: x.id, name: `${x.name} (${x.question_count})` });
                setSubjects((r.result.items || []).map(label));
                setExams((r.result.exams || []).map(label));
            })
            .catch(() => {
                setSubjects([]);
                setExams([]);
            });
    }, []);
    return { subjects, exams };
};

// =========================================================================
// List
// =========================================================================

const filters = [
    <TextInput source="q" label="Search" alwaysOn resettable />,
    <BooleanInput source="mine" label="Only mine" alwaysOn />,
    <SelectInput
        source="status"
        label="Status"
        choices={[
            { id: 'draft', name: 'Draft' },
            { id: 'ready', name: 'Ready' },
            { id: 'archived', name: 'Archived' },
        ]}
    />,
];

/**
 * The empty page replaces the whole list, toolbar included, so it has to carry
 * its own Create button -- without one there is no way to make a first test.
 */
const TestListEmpty = () => (
    <Box sx={{ m: 3 }}>
        <Alert severity="info">
            No tests yet. Create one, add questions from the WiZi bank, mark it ready, then assign it to a class.
        </Alert>
        <Box sx={{ mt: 2 }}>
            <CreateButton variant="contained" label="New test" />
        </Box>
    </Box>
);

const TestList = (props: any) => (
    <List
        title="Tests"
        {...props}
        filters={filters}
        sort={{ field: 'updated_at', order: 'DESC' }}
        exporter={false}
        empty={<TestListEmpty />}
    >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
            <TextField source="name" label="Name" />
            <TextField source="subject_name" label="Subject" sortable={false} />
            <NumberField source="question_count" label="Questions" />
            <NumberField source="total_marks" label="Marks" />
            <FunctionField label="Time" sortBy="time_limit" render={(r: any) => `${r.time_limit} min`} />
            <FunctionField
                label="Status"
                sortBy="status"
                render={(r: any) => (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip size="small" color={STATUS_COLOUR[r.status]} label={r.status} />
                        {r.locked && <Chip size="small" variant="outlined" label="assigned" />}
                    </Box>
                )}
            />
            <FunctionField label="By" sortable={false} render={(r: any) => (r.is_mine ? 'Me' : r.created_by_name)} />
        </Datagrid>
    </List>
);

// =========================================================================
// Details form, shared by create and edit
// =========================================================================

const DetailFields = () => {
    const record = useRecordContext();
    const { subjects, exams } = useTaxonomy();
    // No record = creating. A record the caller does not own is read-only.
    const ro = !!record && !record.can_edit;
    // The language is fixed once there are questions: they were chosen in it.
    const langLocked = !!record && record.question_count > 0;

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                <TextInput source="name" label="Test name" validate={[required()]} disabled={ro} sx={{ flex: 3, minWidth: 260 }} />
                <SelectInput source="exam_id" label="Exam" choices={exams} disabled={ro} emptyText="Any exam" sx={{ flex: 2, minWidth: 180 }} />
            </Box>
            {/* The blueprint: the same three answers a student gives in the
                custom-quiz screen -- exam, subjects, how many. It is what the
                generator draws from, and it can be left empty and the paper
                built by hand instead. */}
            <AutocompleteArrayInput
                source="subject_ids"
                label="Subjects"
                choices={subjects}
                disabled={ro}
                fullWidth
                helperText="Questions are drawn from these. Pick one, or several for a mixed paper."
            />
            <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                <NumberInput
                    source="time_limit"
                    label="Time (minutes)"
                    validate={[required(), number(), minValue(1), maxValue(600)]}
                    disabled={ro}
                    sx={{ flex: 1, minWidth: 140 }}
                />
                <SelectInput
                    source="language"
                    label="Language"
                    choices={[
                        { id: 'en', name: 'English' },
                        { id: 'hi', name: 'Hindi' },
                    ]}
                    defaultValue="en"
                    disabled={ro || langLocked}
                    helperText={langLocked ? 'Fixed: the questions are in this language.' : ' '}
                    sx={{ flex: 1, minWidth: 140 }}
                />
                <SelectInput
                    source="difficulty"
                    label="Difficulty"
                    choices={[
                        { id: 'Elementary', name: 'Easy' },
                        { id: 'Moderate', name: 'Medium' },
                        { id: 'Advance', name: 'Hard' },
                    ]}
                    disabled={ro}
                    emptyText="Any"
                    sx={{ flex: 1, minWidth: 140 }}
                />
                <NumberInput
                    source="target_question_count"
                    label="Number of questions"
                    validate={[number(), minValue(1), maxValue(200)]}
                    disabled={ro}
                    helperText="How many to generate."
                    sx={{ flex: 1, minWidth: 160 }}
                />
                <NumberInput
                    source="passing_percentage"
                    label="Pass mark (%)"
                    validate={[number(), minValue(0), maxValue(100)]}
                    disabled={ro}
                    sx={{ flex: 1, minWidth: 140 }}
                />
            </Box>
            <TextInput source="instructions" label="Instructions for students" multiline minRows={2} disabled={ro} fullWidth />
            <TextInput source="description" label="Notes (teachers only)" multiline disabled={ro} fullWidth />
        </>
    );
};

const TestCreate = (props: any) => (
    <Create {...props} title="New test" transform={transform} redirect="edit">
        <SimpleForm toolbar={<FormToolbar {...props} />} defaultValues={{ language: 'en', time_limit: 30, target_question_count: 20, subject_ids: [] }}>
            <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                Save the details first. Then you can generate the questions from the WiZi bank in one
                click, and add or remove any of them by hand.
            </Alert>
            <DetailFields />
        </SimpleForm>
    </Create>
);

// =========================================================================
// Edit -- the builder
// =========================================================================

/** Status moves, duplicate, delete, assign. Each is its own API call with its own rule. */
const TestActions = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    if (!record) return null;

    const status = async (next: string, confirm?: string) => {
        if (confirm && !window.confirm(confirm)) return;
        try {
            await schoolApi.put(`school-test/${record.id}`, { status: next });
            notify(`The test is now ${next}.`, { type: 'success' });
            refresh();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };
    const duplicate = async () => {
        try {
            const r = await schoolApi.post(`school-test/duplicate/${record.id}`, {});
            notify('Copied. This is your own draft.', { type: 'success' });
            navigate(`/school-test/${r.result.id}`);
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };
    const remove = async () => {
        if (!window.confirm(`Delete "${record.name}"? This cannot be undone.`)) return;
        try {
            await schoolApi.del(`school-test/${record.id}`);
            notify('Test deleted.', { type: 'success' });
            navigate('/school-test');
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const mine = record.is_mine;
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: '100%', mb: 2 }}>
            <Chip color={STATUS_COLOUR[record.status]} label={record.status} />
            {record.locked && <Chip variant="outlined" label="assigned — paper frozen" />}
            {!mine && <Typography variant="body2" color="text.secondary">by {record.created_by_name}</Typography>}
            <Box sx={{ flex: 1 }} />
            {mine && record.status === 'draft' && (
                <Button variant="contained" color="success" disabled={record.question_count === 0} onClick={() => status('ready')}>
                    Mark ready
                </Button>
            )}
            {mine && record.status === 'ready' && (
                <Button variant="contained" onClick={() => navigate(`/assignment/create?quiz=school:${record.id}`)}>
                    Assign to a class
                </Button>
            )}
            {mine && record.status === 'ready' && !record.locked && <Button onClick={() => status('draft')}>Back to draft</Button>}
            {mine && record.status !== 'archived' && (
                <Button onClick={() => status('archived', 'Archive this test? It leaves the lists; results that use it are kept.')}>Archive</Button>
            )}
            {mine && record.status === 'archived' && (
                <Button onClick={() => status(record.question_count > 0 ? 'ready' : 'draft')}>Restore</Button>
            )}
            <Button variant="outlined" onClick={duplicate}>
                Duplicate
            </Button>
            {mine && !record.locked && (
                <Button color="error" onClick={remove}>
                    Delete
                </Button>
            )}
        </Box>
    );
};

const TestPanels = () => {
    const record = useRecordContext();
    const refresh = useRefresh();
    if (!record) return null;
    return (
        <Box sx={{ p: 2 }}>
            {record.can_edit_questions && <GeneratePanel test={record} onChange={refresh} />}
            <PaperPanel test={record} onChange={refresh} />
            {record.can_edit_questions && (
                <BankPanel testId={Number(record.id)} language={record.language} subjectId={record.subject_id} onAdded={refresh} />
            )}
        </Box>
    );
};

const EditToolbar = (props: any) => {
    const record = useRecordContext();
    return <FormToolbar {...props} hideSave={!!record && !record.can_edit} />;
};

const TestEdit = (props: any) => (
    <Edit {...props} title="Test" mutationMode="pessimistic" transform={transform} redirect={false}>
        <SimpleForm toolbar={<EditToolbar {...props} />}>
            <TestActions />
            <DetailFields />
        </SimpleForm>
        {/* Outside the form: pressing Enter in a marks box must not submit the details. */}
        <TestPanels />
    </Edit>
);

export default { list: TestList, create: TestCreate, edit: TestEdit };
